/*
  Babblee · shell behaviour
  ===========================================================================
  The rotating announcement, the header's stuck state, the search shortcut, and
  the drift's randomisation. Small things, but they are the ones that decide
  whether the top of the page feels alive or printed.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------------------------
     Announcement rotator
     ---------------------------------------------------------------------- */
  const rotator = document.querySelector('[data-bb-rotator]');
  const messages = rotator ? [...rotator.querySelectorAll('[data-bb-msg]')] : [];
  if (messages.length > 1) {
    let at = 0;
    let timer = null;

    const advance = () => {
      const from = messages[at];
      at = (at + 1) % messages.length;
      const to = messages[at];
      from.classList.remove('is-active');
      from.classList.add('is-leaving');
      to.classList.remove('is-leaving');
      to.classList.add('is-active');
      // Clear the outgoing state once it is off screen, so a message returning
      // to the top of the loop does not start from the leaving position.
      setTimeout(() => from.classList.remove('is-leaving'), 600);
    };

    const start = () => { if (!timer) timer = setInterval(advance, 5200); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    start();
    // A rotator that keeps rotating in a background tab is work nobody sees.
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    // Pausing on hover is the difference between a promise you can read and one
    // that leaves while you are reading it.
    rotator.closest('[data-bb-announce]')?.addEventListener('pointerenter', stop);
    rotator.closest('[data-bb-announce]')?.addEventListener('pointerleave', start);
  }

  /* -------------------------------------------------------------------------
     Live dispatch ticker
     -------------------------------------------------------------------------
     The same arithmetic as the product page's delivery estimate, deliberately:
     if the bar and the product page disagreed about when a parcel leaves, one
     of them would be lying on every page view. Weekends are not dispatch days,
     so after Friday's cutoff the answer is Monday rather than "tomorrow".
     ---------------------------------------------------------------------- */
  const live = document.querySelector('[data-bb-announce-live]');
  if (live) {
    const slot = live.querySelector('[data-bb-announce-live-text]');
    const CUTOFF = Number(live.dataset.bbCutoff || 15);
    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    const nextWorkingDay = (from) => {
      const d = new Date(from);
      do { d.setDate(d.getDate() + 1); } while (isWeekend(d));
      return d;
    };
    const dayName = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { weekday: 'long' });

    const paint = () => {
      const now = new Date();
      const open = now.getHours() < CUTOFF && !isWeekend(now);
      if (open) {
        const cutoff = new Date(now);
        cutoff.setHours(CUTOFF, 0, 0, 0);
        const ms = cutoff - now;
        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        const sec = Math.floor((ms % 60000) / 1000);
        const left = h > 0
          ? `${h}h ${String(m).padStart(2, '0')}m`
          : `${m}m ${String(sec).padStart(2, '0')}s`;
        live.classList.remove('is-closed');
        if (slot) slot.innerHTML = `Order within <b>${left}</b> for today's dispatch`;
      } else {
        live.classList.add('is-closed');
        if (slot) slot.textContent = `Next dispatch ${dayName.format(nextWorkingDay(now))}`;
      }
    };

    paint();
    let ticker = null;
    const run = (on) => {
      if (on && !ticker) ticker = setInterval(paint, 1000);
      if (!on && ticker) { clearInterval(ticker); ticker = null; }
    };
    run(true);
    // A countdown ticking in a background tab is work nobody sees.
    document.addEventListener('visibilitychange', () => run(!document.hidden));
  }

  /* Scatter the drift. Done here rather than in CSS because seven hand-written
     nth-child rules is seven values that have to be kept apart by hand, and the
     point of the effect is that no two are on the same rhythm. */
  const drift = document.querySelector('.bb-announce__drift');
  if (drift && !reduced.matches) {
    [...drift.children].forEach((dot, i) => {
      const size = 6 + ((i * 5) % 9);
      dot.style.left = `${6 + ((i * 14.3) % 88)}%`;
      dot.style.width = `${size}px`;
      dot.style.height = `${size}px`;
      dot.style.animationDuration = `${7 + ((i * 1.7) % 6)}s`;
      dot.style.animationDelay = `${(i * 1.3) % 7}s`;
    });
  }

  /* -------------------------------------------------------------------------
     Header stuck state
     ---------------------------------------------------------------------- */
  const head = document.querySelector('.bb-head');
  if (head) {
    /* This theme does not scroll the window: `html` and `body` are both
       overflow:hidden at viewport height and an inner `.page-wrapper` does the
       scrolling. A scroll listener on `window` here would never fire once. */
    const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
    const onScroll = () => head.classList.toggle('is-stuck', (scroller?.scrollTop || 0) > 6);
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* -------------------------------------------------------------------------
     Search shortcut
     ---------------------------------------------------------------------- */
  const search = document.querySelector('[data-bb-search-input]');
  if (search) {
    document.addEventListener('keydown', (e) => {
      // Never steal a keystroke that was going into a field.
      const target = e.target;
      const typing = target instanceof HTMLElement
        && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        search.focus();
        search.select();
        return;
      }
      // "/" is the other search shortcut people try, and it costs nothing.
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        search.focus();
        return;
      }
      if (e.key === 'Escape' && document.activeElement === search) search.blur();
    });

    // Show the right shortcut for the machine it is being read on.
    const kbd = document.querySelector('[data-bb-kbd]');
    if (kbd && !/Mac|iPhone|iPad/.test(navigator.platform || '')) kbd.textContent = 'Ctrl K';
  }
})();

/*
  Newsletter
  ---------------------------------------------------------------------------
  The form posts natively, because the Shopify customer endpoint is what
  actually creates the subscriber. This adds the two things a native post does
  not: it refuses to submit an address the browser can already tell is wrong,
  so a shopper is not sent on a round trip to be told so, and it marks the
  button as sending, so the press has an answer before the page moves.
*/
(() => {
  const field = document.querySelector('[data-bb-newsletter]');
  if (!field) return;
  const form = field.closest('form');
  const input = field.querySelector('[data-bb-newsletter-input]');
  const error = document.querySelector('[data-bb-newsletter-error]');
  if (!form || !input) return;

  const showError = (message) => {
    if (!error) return;
    error.hidden = false;
    const slot = error.querySelector('span');
    if (slot && message) slot.textContent = message;
  };
  const clearError = () => { if (error) error.hidden = true; };

  input.addEventListener('input', clearError);

  form.addEventListener('submit', (e) => {
    const value = input.value.trim();
    if (!value) {
      e.preventDefault();
      showError('Enter an email address so we know where to send it.');
      input.focus();
      return;
    }
    // checkValidity rather than a regex: the browser's idea of a valid address
    // is the one the server will also accept, and every hand-rolled pattern
    // eventually rejects somebody's real address.
    if (!input.checkValidity()) {
      e.preventDefault();
      showError('That address is missing something — check for a typo?');
      input.focus();
      return;
    }
    clearError();
    field.classList.add('is-sending');
  });
})();

/*
  Cart count
  ---------------------------------------------------------------------------
  The header badge was rendered from Liquid at page load and never touched
  again, so adding something to the cart left it showing the old number until
  the next navigation — on the one surface where a shopper is checking whether
  the thing they just pressed worked.

  Every add path already fires `cart:refresh`; this listens for it and asks the
  cart for the truth rather than incrementing a number it cannot verify.
*/
(() => {
  const badge = document.querySelector('[data-bb-cart-count]');
  if (!badge) return;

  const paint = (count) => {
    badge.textContent = String(count);
    badge.hidden = count < 1;
    if (count > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      badge.style.animation = 'none';
      void badge.offsetWidth;
      badge.style.animation = '';
    }
  };

  const refresh = async () => {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      paint(Number(cart.item_count) || 0);
    } catch { /* leave the last known number rather than showing a wrong one */ }
  };

  document.addEventListener('cart:refresh', refresh);
  // Coming back via the back button serves a cached page with a stale count.
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
})();
